import React, { useEffect, useState } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { Sparkles, Loader2, Tv, Landmark, ShieldCheck, TrendingUp, TrendingDown, Minus, Crown, ExternalLink, Check } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { PARTY_DOT, money, num } from "./tracking";

const LINE_COLORS = { Democratic: "var(--partisan-blue)", Republican: "var(--partisan-red)", Independent: "var(--accent-primary)", Libertarian: "var(--warning)", Green: "var(--success)", Other: "var(--text-muted)" };

export const Section = ({ eyebrow, title, badge, children, testid, className = "" }) => (
  <section className={`rounded-3xl border border-white/10 bg-white/[0.03] p-5 md:p-6 ${className}`} data-testid={testid}>
    <div className="flex items-start justify-between gap-3 mb-4">
      <div>
        {eyebrow && <div className="text-[10px] uppercase tracking-[0.2em] text-amber-300/80 font-medium">{eyebrow}</div>}
        <h2 className="font-display text-base md:text-lg font-bold mt-0.5">{title}</h2>
      </div>
      {badge && <span className="shrink-0 text-[10px] px-2 py-0.5 rounded-full border border-amber-400/30 bg-amber-400/10 text-amber-300 uppercase tracking-wide">{badge}</span>}
    </div>
    {children}
  </section>
);

export const RaceHeader = ({ data }) => (
  <div data-testid="showcase-header">
    <div className="text-[11px] uppercase tracking-[0.25em] text-amber-300 font-medium">Your race demo · 2026 Midterms</div>
    <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mt-2">{data.race.label}</h1>
    <div className="flex flex-wrap items-center gap-3 mt-3 text-sm text-slate-400">
      <span className="inline-flex items-center gap-1.5 rounded-full bg-white/5 px-3 py-1" data-testid="showcase-countdown"><span className="font-display font-bold text-amber-300 text-base">{data.days_until}</span> days to Election Day</span>
      <span>{data.candidates.length} declared candidates</span>
      <span>{data.race.state_name}</span>
    </div>
  </div>
);

const Trend = ({ t }) => !t ? null : (
  <span className={`inline-flex items-center gap-1 text-[11px] ${t.direction === "rising" ? "text-emerald-300" : t.direction === "falling" ? "text-rose-300" : "text-slate-400"}`}>
    {t.direction === "rising" ? <TrendingUp className="w-3 h-3" /> : t.direction === "falling" ? <TrendingDown className="w-3 h-3" /> : <Minus className="w-3 h-3" />}{t.current_support}%
  </span>
);

export const CandidatesCard = ({ data }) => (
  <Section eyebrow="Race Intelligence" title="Who's in the race — and who has the money" badge="Live FEC" testid="showcase-candidates">
    <div className="divide-y divide-white/5">
      {data.candidates.map((c) => (
        <div key={c.id} className="py-3 flex items-center gap-3" data-testid={`showcase-candidate-${c.id}`}>
          <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${PARTY_DOT[c.party] || "bg-slate-400"}`} />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-white flex items-center gap-2 flex-wrap">{c.display_name}{c.incumbent && <span className="inline-flex items-center gap-1 text-[10px] text-amber-300"><Crown className="w-3 h-3" /> Incumbent</span>}</div>
            <div className="text-[11px] text-slate-400">{c.party}{c.finance?.coverage_end_date ? ` · filed through ${c.finance.coverage_end_date}` : ""}</div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-sm font-semibold tabular-nums">{money(c.finance?.receipts)}</div>
            <div className="text-[10px] text-slate-500">raised · cash {money(c.finance?.cash_on_hand)}</div>
          </div>
          <Trend t={c.tracking} />
        </div>
      ))}
    </div>
    <div className="text-[11px] text-slate-500 mt-3">Source: FEC filings via OpenFEC — figures reflect the last reporting period. Inside TotalReach this refreshes daily with change alerts.</div>
  </Section>
);

export const PollTrendCard = ({ data }) => {
  const tracked = data.candidates.filter((c) => data.momentum.some((m) => m.fec_candidate_id === c.fec_candidate_id));
  const byDate = {};
  data.polls.forEach((p) => {
    const d = (byDate[p.date] = byDate[p.date] || { date: p.date.slice(5), _n: {} });
    Object.entries(p.samples).forEach(([cid, v]) => { d[cid] = (d[cid] || 0) + v; d._n[cid] = (d._n[cid] || 0) + 1; });
  });
  const rows = Object.values(byDate).map((d) => { const r = { date: d.date }; Object.keys(d._n).forEach((cid) => { r[cid] = +(d[cid] / d._n[cid]).toFixed(1); }); return r; });
  return (
    <Section eyebrow="Polling engine" title="10-week tracking trend" badge="Demo data" testid="showcase-polls">
      {tracked.length < 2 ? <div className="text-sm text-slate-500 py-6 text-center">Tracking starts once a race has two or more declared candidates.</div> : (
        <div className="h-60">
          <ResponsiveContainer width="100%" height="100%" minWidth={0}>
            <LineChart data={rows}>
              <CartesianGrid stroke="rgb(var(--contrast-rgb) / 0.06)" />
              <XAxis dataKey="date" tick={{ fill: "var(--text-muted)", fontSize: 11 }} />
              <YAxis domain={["auto", "auto"]} tick={{ fill: "var(--text-muted)", fontSize: 11 }} unit="%" width={40} />
              <Tooltip contentStyle={{ background: "var(--bg-elevated)", border: "1px solid rgb(var(--contrast-rgb) / 0.1)", borderRadius: 8, fontSize: 12 }} />
              <Legend formatter={(v) => tracked.find((c) => c.fec_candidate_id === v)?.display_name.split(" ").slice(-1)[0] || v} />
              {tracked.map((c) => <Line key={c.fec_candidate_id} type="monotone" dataKey={c.fec_candidate_id} stroke={LINE_COLORS[c.party] || LINE_COLORS.Other} strokeWidth={2} dot={false} />)}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
      <div className="text-[11px] text-slate-500 mt-3">Illustrative tracking, not a real poll. Inside TotalReach you import your own polls (CSV) and we blend live public feeds — with weighted averages and momentum alerts.</div>
    </Section>
  );
};

export const CtvReachCard = ({ est }) => (
  <Section eyebrow="CTV Voter Universe Planner" title="What a $25k / 4-week CTV flight reaches here" badge="Modeled" testid="showcase-ctv">
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {[["Voter universe", num(est.universe_households), "households"], ["Reached", num(est.reached_households), `${est.reach_pct}% of universe`],
        ["Avg frequency", `${est.avg_frequency}×`, `${num(est.impressions)} impressions`], ["Cord-cutter homes", num(est.ctv_only_households), "unreachable by cable"]].map(([l, v, s]) => (
        <div key={l} className="rounded-xl bg-white/5 p-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">{l}</div>
          <div className="font-display text-xl font-bold text-amber-300 mt-0.5">{v}</div>
          <div className="text-[11px] text-slate-400">{s}</div>
        </div>
      ))}
    </div>
    <div className="text-[11px] text-slate-500 mt-3 flex items-start gap-1.5"><Tv className="w-3.5 h-3.5 shrink-0 mt-0.5" /> {est.note} Live plans use real district household counts and 243 political audience segments.</div>
  </Section>
);

export const AiIdeasCard = ({ raceId, initial }) => {
  const [ideas, setIdeas] = useState(initial || null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const load = async () => {
    setBusy(true); setErr("");
    try { const { data } = await api.post(`/public/scan/race/${raceId}/ideas`); setIdeas(data.ideas); }
    catch (e) { setErr(errMsg(e, "AI is busy — try again in a moment")); }
    finally { setBusy(false); }
  };
  useEffect(() => { if (!initial) load(); }, [raceId]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Section eyebrow="AI strategist" title="Three plays built for this race" badge="Claude AI" testid="showcase-ideas">
      {busy && <div className="text-sm text-slate-400 flex items-center gap-2 py-4" data-testid="showcase-ideas-loading"><Loader2 className="w-4 h-4 animate-spin" /> Reading the race and drafting plays…</div>}
      {err && <div className="text-sm text-rose-300 flex items-center gap-3">{err} <button onClick={load} className="underline" data-testid="showcase-ideas-retry">Retry</button></div>}
      {ideas && !busy && (
        <div className="grid md:grid-cols-3 gap-3">
          {ideas.map((i, k) => (
            <div key={k} className="rounded-2xl border border-white/10 bg-elevated p-4 flex flex-col gap-2" data-testid={`showcase-idea-${k}`}>
              <div className="flex items-center justify-between gap-2"><span className="text-[10px] uppercase tracking-wider text-amber-300">{i.channel}</span><span className="text-[10px] text-slate-500">{i.urgency}</span></div>
              <div className="font-semibold text-sm text-white">{i.title}</div>
              <div className="text-xs text-slate-400 leading-relaxed">{i.rationale}</div>
              <div className="text-xs text-slate-300 border-t border-white/5 pt-2 mt-auto"><Sparkles className="w-3 h-3 inline mr-1 text-amber-300" />{i.suggested_play}</div>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
};

export const FundraisingPreviewCard = ({ f }) => (
  <Section eyebrow="Fundraising & compliance" title="A donation page, ready in minutes" testid="showcase-fundraising">
    <div className="grid md:grid-cols-5 gap-4">
      <div className="md:col-span-3 rounded-2xl overflow-hidden border border-white/10 bg-elevated p-5">
        <div className="text-[10px] uppercase tracking-widest text-amber-300/80">totalreach.us{f.microsite_url}</div>
        <div className="font-display text-2xl font-bold mt-2">{f.headline}</div>
        <div className="text-sm text-slate-400 mt-1">{f.subline}</div>
        <div className="grid grid-cols-3 gap-2 mt-4">{f.suggested_amounts.map((a) => <div key={a} className={`rounded-lg border text-center py-2 text-sm font-semibold ${a === 50 ? "border-amber-400 bg-amber-400/10 text-amber-200" : "border-white/10 text-slate-300"}`}>${a}</div>)}</div>
        <div className="mt-4 rounded-full bg-gradient-to-r from-amber-400 to-amber-600 text-on-accent text-center font-bold py-2.5 text-sm">Contribute $50</div>
        <div className="text-[10px] text-slate-500 mt-3 leading-relaxed" data-testid="showcase-disclaimer">{f.disclaimer}</div>
      </div>
      <div className="md:col-span-2 space-y-3 text-sm">
        {[["FEC-compliant checkout", "Employer / occupation, attestations and the $3,500 limit enforced automatically."],
          ["Text-to-Give", f.text_to_give + " — keyword → checkout link in seconds."],
          ["Donor CRM + receipts", "Every gift lands in your CRM; receipts and 24-hour thank-yous go out on their own."],
          ["Disclaimer builder", "Federal & state rules, per medium — versioned and audit-ready."]].map(([t, s]) => (
          <div key={t} className="flex gap-2"><Check className="w-4 h-4 text-emerald-300 shrink-0 mt-0.5" /><div><div className="font-semibold text-white">{t}</div><div className="text-xs text-slate-400">{s}</div></div></div>
        ))}
      </div>
    </div>
  </Section>
);

const WYG_ICONS = [Landmark, Tv, Sparkles, Landmark, ShieldCheck, TrendingUp];

export const WhatYouGet = ({ items }) => (
  <Section eyebrow="Inside the platform" title="One login for the whole campaign" testid="showcase-what-you-get">
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {items.map((i, k) => {
        const Icon = WYG_ICONS[k % WYG_ICONS.length];
        return (
          <div key={k} className="rounded-2xl bg-white/5 p-4" data-testid={`what-you-get-tile-${k}`}>
            <div className="flex items-center gap-2 text-sm font-semibold text-white"><Icon className="w-4 h-4 text-amber-300" />{i.title}</div>
            <div className="text-xs text-slate-400 mt-1.5 leading-relaxed">{i.text}</div>
          </div>
        );
      })}
    </div>
  </Section>
);

export const Sources = ({ sources }) => (
  <div className="text-[11px] text-slate-500 flex flex-wrap gap-x-4 gap-y-1" data-testid="showcase-sources">
    {sources.map((s, i) => s.source_url ? <a key={i} href={s.source_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-slate-300">{s.source} <ExternalLink className="w-3 h-3" /></a> : <span key={i}>{s.source}</span>)}
  </div>
);
